// Supabase Client for time4subs Forward Command
import { createClient } from "@supabase/supabase-js";
import { assertNoOpenEntries, getOpenEntries, needsOfficeReview, openShiftMessage, saveClockOut } from "./clockSafety.mjs";

// These come from your Supabase project settings
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// JOBS
// ============================================

/**
 * Get all active jobs for the organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of jobs
 */
export async function getActiveJobs(orgId) {
  const { data, error } = await supabase
    .from("t4s_jobs")
    .select(
      `
      id,
      job_number,
      name,
      address,
      city,
      state,
      latitude,
      longitude,
      timezone,
      status,
      start_date,
      estimated_days,
      cost_code_template,
      customer:t4s_customers(name)
    `,
    )
    .eq("org_id", orgId)
    .eq("status", "active")
    .eq("time_tracking_enabled", true)
    .order("job_number");

  if (error) throw error;
  return data;
}

/**
 * Get all jobs for the organization (including inactive)
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of all jobs
 */
export async function getAllJobs(orgId) {
  const { data, error } = await supabase
    .from("t4s_jobs")
    .select(
      `
      id,
      job_number,
      name,
      address,
      city,
      state,
      latitude,
      longitude,
      timezone,
      status,
      start_date,
      estimated_days,
      cost_code_template,
      customer:t4s_customers(name)
    `,
    )
    .eq("org_id", orgId)
    .eq("time_tracking_enabled", true)
    .order("job_number");

  if (error) throw error;
  return data;
}

/**
 * Get a single job by ID
 * @param {string} jobId - Job ID
 * @returns {Promise<Object>} Job details
 */
export async function getJob(jobId) {
  const { data, error } = await supabase
    .from("t4s_jobs")
    .select(
      `
      *,
      customer:t4s_customers(name, contact_name, contact_phone)
    `,
    )
    .eq("id", jobId)
    .single();

  if (error) throw error;
  return data;
}

// ============================================
// EMPLOYEES & ASSIGNMENTS
// ============================================

/**
 * Get today's date in a specific timezone
 * @param {string} timezone - IANA timezone (e.g., 'America/Denver')
 * @returns {string} Date in YYYY-MM-DD format
 */
function getTodayInTimezone(timezone = "America/Denver") {
  return new Date().toLocaleDateString("en-CA", { timeZone: timezone });
}

/**
 * Get today's active job assignments for a foreman/employee
 * Uses each job's timezone to determine "today" for accurate matching
 * @param {string} orgId - Organization ID
 * @param {string} employeeId - Employee ID
 * @returns {Promise<Array>} List of jobs assigned for today (in each job's timezone)
 */
export async function getForemanAssignments(orgId, employeeId) {
  // Fetch assignments from a 3-day window to handle timezone edge cases
  // (yesterday, today, tomorrow in local time covers all possible timezone offsets)
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const startDate = yesterday.toLocaleDateString("en-CA");
  const endDate = tomorrow.toLocaleDateString("en-CA");

  const { data: assignments, error: assignError } = await supabase
    .from("t4s_job_assignments")
    .select(
      `
      id,
      assigned_date,
      job:t4s_jobs!job_id(
        id,
        job_number,
        name,
        address,
        city,
        state,
        latitude,
        longitude,
        timezone,
        status,
        start_date,
        estimated_days,
        cost_code_template
      )
    `,
    )
    .eq("employee_id", employeeId)
    .gte("assigned_date", startDate)
    .lte("assigned_date", endDate);

  if (assignError) {
    console.error("Error fetching foreman assignments:", assignError);
    throw assignError;
  }

  // Filter to assignments where assigned_date matches "today" in the job's timezone
  const activeJobs = (assignments || [])
    .filter((a) => {
      if (!a.job || a.job.status !== "active") return false;
      const todayInJobTz = getTodayInTimezone(
        a.job.timezone || "America/Denver",
      );
      return a.assigned_date === todayInJobTz;
    })
    .map((a) => a.job);

  return activeJobs;
}

/**
 * Format time in a specific timezone
 * @param {string} isoTimeStr - ISO timestamp string
 * @param {string} timezone - IANA timezone (e.g., 'America/Denver')
 * @returns {string} Formatted time string (e.g., "8:30 AM")
 */
function formatTimeInTimezone(isoTimeStr, timezone) {
  if (!isoTimeStr) return null;
  const date = new Date(isoTimeStr);
  return date.toLocaleTimeString("en-US", {
    timeZone: timezone || "America/Denver",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Get today's crew for a job
 * @param {string} jobId - Job ID
 * @param {string} date - Date in YYYY-MM-DD format (defaults to today)
 * @param {string} jobTimezone - Job's timezone for displaying times
 * @returns {Promise<Array>} List of assigned employees with time entries
 */
export async function getTodaysCrew(
  jobId,
  date = new Date().toLocaleDateString("en-CA"), // YYYY-MM-DD in local timezone
  jobTimezone = "America/Denver",
) {
  const { data, error } = await supabase
    .from("t4s_job_assignments")
    .select(
      `
      id,
      employee:t4s_employees!employee_id(
        id,
        first_name,
        last_name,
        display_name,
        role,
        avatar_color
      )
    `,
    )
    .eq("job_id", jobId)
    .eq("assigned_date", date);

  if (error) {
    console.error("getTodaysCrew: assignments error", error);
    throw error;
  }

  // Get time entries for these employees on this job today
  const employeeIds = data.map((a) => a.employee.id);

  let timeEntries = [];
  if (employeeIds.length > 0) {
    const { data: teData, error: teError } = await supabase
      .from("t4s_time_entries")
      .select(
        `
        *,
        allocations:t4s_time_allocations(
          id,
          cost_code_id,
          hours,
          cost_code:t4s_cost_codes(id, code, name)
        )
      `,
      )
      .eq("job_id", jobId)
      .eq("work_date", date)
      .neq("status", "removed")
      .in("employee_id", employeeIds);

    if (teError) {
      console.error("getTodaysCrew: time entries error", teError);
      throw teError;
    }
    timeEntries = teData || [];
  }

  // Include recent overnight shifts; surface older/cross-job punches for explicit review.
  const openEntries = await getOpenEntries(supabase, employeeIds);

  // Merge time entries with employees
  return data.map((assignment) => {
    const employeeOpen = openEntries.filter(te => te.employee_id === assignment.employee.id);
    const unresolved = employeeOpen.find(te => te.job_id !== jobId || needsOfficeReview(te)) || (employeeOpen.length > 1 ? employeeOpen[0] : null);
    const todayEntry = timeEntries?.find(te => te.employee_id === assignment.employee.id);
    const entry = todayEntry || employeeOpen.find(te => te.job_id === jobId && !needsOfficeReview(te));

    // Map allocations to the format expected by the UI
    const allocations = (entry?.allocations || []).map((a) => ({
      costCode: a.cost_code?.code,
      costCodeId: a.cost_code_id,
      costName: a.cost_code?.name,
      hours: a.hours,
    }));

    return {
      ...assignment.employee,
      timeEntry: entry || null,
      openShiftWarning: unresolved ? openShiftMessage(unresolved) : null,
      clockIn: formatTimeInTimezone(entry?.clock_in, jobTimezone),
      clockOut: formatTimeInTimezone(entry?.clock_out, jobTimezone),
      totalHours: entry?.total_hours || 0,
      allocations,
    };
  });
}

/**
 * Get all employees for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of employees
 */
export async function getEmployees(orgId) {
  const { data, error } = await supabase
    .from("t4s_employees")
    .select("*")
    .eq("org_id", orgId)
    .eq("is_active", true)
    .order("last_name");

  if (error) throw error;
  return data;
}

// ============================================
// TIME ENTRIES
// ============================================

/**
 * Get work date in a specific timezone
 * @param {string} timezone - IANA timezone (e.g., 'America/Denver')
 * @returns {string} Date in YYYY-MM-DD format for that timezone
 */
function getWorkDateInTimezone(timezone) {
  const now = new Date();
  // Use Intl.DateTimeFormat to get the date in the job's timezone
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone || "America/Denver",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(now); // Returns YYYY-MM-DD
}

/**
 * Clock an employee in
 * @param {Object} params - Clock in parameters
 * @returns {Promise<Object>} Created time entry
 */
export async function clockIn({
  orgId,
  employeeId,
  jobId,
  jobTimezone,
  latitude,
  longitude,
  photoUrl,
  enteredBy,
}) {
  await assertNoOpenEntries(supabase, [employeeId]);
  const now = new Date();
  // Use job's timezone for work_date calculation (defaults to Mountain Time)
  const workDate = getWorkDateInTimezone(jobTimezone || "America/Denver");

  const { data, error } = await supabase
    .from("t4s_time_entries")
    .insert({
      org_id: orgId,
      employee_id: employeeId,
      job_id: jobId,
      work_date: workDate,
      clock_in: now.toISOString(),
      clock_in_latitude: latitude,
      clock_in_longitude: longitude,
      clock_in_photo_url: photoUrl,
      entered_by: enteredBy,
      status: "active",
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update a time entry (edit clock in/out times)
 * @param {string} timeEntryId - Time entry ID
 * @param {Object} params - Update parameters
 * @returns {Promise<Object>} Updated time entry
 */
export async function updateTimeEntry(
  timeEntryId,
  {
    clockIn,
    clockOut,
    jobTimezone = "America/Denver",
    editedBy = null,
    editSource = "manual",
  },
) {
  // Helper to convert "HH:MM" time string to ISO timestamp in job's timezone
  const timeStringToISO = (timeStr) => {
    if (!timeStr) return null;
    // If already a full timestamp, use it
    if (timeStr.includes("T") || timeStr.includes("-")) {
      return timeStr;
    }
    // Parse "HH:MM" format (24-hour from time input)
    const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);

    // Get today's date in the job's timezone
    const now = new Date();
    const dateInTz = new Intl.DateTimeFormat("en-CA", {
      timeZone: jobTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);

    // Build datetime string and use Intl to get the correct UTC time
    // This avoids manual offset calculations which are error-prone
    const timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;

    // Parse the date parts and create a UTC timestamp
    // by calculating the offset for the job's timezone
    const [year, month, day] = dateInTz.split("-").map(Number);

    // Create a date object for this specific time in the job timezone
    // We use a trick: format the same instant in both UTC and job TZ to get offset
    const tempDate = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, 0),
    );
    const utcString = tempDate.toLocaleString("en-US", { timeZone: "UTC" });
    const tzString = tempDate.toLocaleString("en-US", {
      timeZone: jobTimezone,
    });
    const utcParsed = new Date(utcString);
    const tzParsed = new Date(tzString);
    const offsetMs = tzParsed - utcParsed;

    // The user entered time in job timezone, so subtract offset to get UTC
    const utcTime = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, 0) - offsetMs,
    );

    return utcTime.toISOString();
  };

  // Fetch current values for audit trail
  const { data: currentEntry, error: fetchError } = await supabase
    .from("t4s_time_entries")
    .select(
      "clock_in, clock_out, org_id, original_clock_in, original_clock_out",
    )
    .eq("id", timeEntryId)
    .single();

  if (fetchError) throw fetchError;

  const updates = {};
  const editHistory = [];

  if (clockIn) {
    const newClockIn = timeStringToISO(clockIn);
    if (newClockIn !== currentEntry.clock_in) {
      updates.clock_in = newClockIn;
      // Save original value on first edit
      if (!currentEntry.original_clock_in && currentEntry.clock_in) {
        updates.original_clock_in = currentEntry.clock_in;
      }
      editHistory.push({
        field: "clock_in",
        oldValue: currentEntry.clock_in,
        newValue: newClockIn,
      });
    }
  }
  if (clockOut) {
    const newClockOut = timeStringToISO(clockOut);
    if (newClockOut !== currentEntry.clock_out) {
      updates.clock_out = newClockOut;
      // Save original value on first edit
      if (!currentEntry.original_clock_out && currentEntry.clock_out) {
        updates.original_clock_out = currentEntry.clock_out;
      }
      editHistory.push({
        field: "clock_out",
        oldValue: currentEntry.clock_out,
        newValue: newClockOut,
      });
    }
  }

  // Mark as edited if changes were made
  if (editHistory.length > 0 && editedBy) {
    updates.edited_by = editedBy;
    updates.edited_at = new Date().toISOString();
  }

  // Only update if there are changes
  if (Object.keys(updates).length === 0) {
    return currentEntry;
  }

  const { data, error } = await supabase
    .from("t4s_time_entries")
    .update(updates)
    .eq("id", timeEntryId)
    .select()
    .single();

  if (error) throw error;

  // Log edit history
  if (editHistory.length > 0) {
    for (const edit of editHistory) {
      await supabase.from("t4s_time_entry_edit_history").insert({
        time_entry_id: timeEntryId,
        org_id: currentEntry.org_id,
        edited_by: editedBy,
        field_name: edit.field,
        old_value: edit.oldValue,
        new_value: edit.newValue,
        edit_source: editSource,
      });
    }
  }

  return data;
}

/**
 * Get edit history for a time entry
 * @param {string} timeEntryId - Time entry ID
 * @returns {Promise<Array>} List of edit history records
 */
export async function getTimeEntryEditHistory(timeEntryId) {
  const { data, error } = await supabase
    .from("t4s_time_entry_edit_history")
    .select(
      `
      *,
      editor:t4s_employees!edited_by(id, display_name, first_name, last_name)
    `,
    )
    .eq("time_entry_id", timeEntryId)
    .order("edited_at", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Clock an employee out
 * @param {string} timeEntryId - Time entry ID
 * @param {Object} params - Clock out parameters
 * @returns {Promise<Object>} Updated time entry
 */
export async function clockOut(timeEntryId, details = {}) {
  return saveClockOut(supabase, timeEntryId, details);
}

/**
 * Bulk clock in multiple employees (no photos)
 * @param {Object} params - Bulk clock in parameters
 * @returns {Promise<Array>} Created time entries
 */
export async function bulkClockIn({
  orgId,
  employeeIds,
  jobId,
  jobTimezone,
  enteredBy,
}) {
  await assertNoOpenEntries(supabase, employeeIds);
  const now = new Date();
  // Use job's timezone for work_date calculation (defaults to Mountain Time)
  const workDate = getWorkDateInTimezone(jobTimezone || "America/Denver");

  const entries = employeeIds.map((employeeId) => ({
    org_id: orgId,
    employee_id: employeeId,
    job_id: jobId,
    work_date: workDate,
    clock_in: now.toISOString(),
    entered_by: enteredBy,
    status: "active",
  }));

  const { data, error } = await supabase
    .from("t4s_time_entries")
    .insert(entries)
    .select();

  if (error) {
    console.error("bulkClockIn error:", error);
    throw new Error(error.message || JSON.stringify(error));
  }

  return data;
}

/**
 * Bulk clock out multiple employees
 * @param {Array<string>} timeEntryIds - Time entry IDs to clock out
 * @returns {Promise<Array>} Updated time entries
 */
export async function bulkClockOut(timeEntryIds) {
  const clockOut = new Date().toISOString();
  const results = await Promise.allSettled(timeEntryIds.map(id => saveClockOut(supabase, id, { clockOut })));
  const failures = results.filter(result => result.status === 'rejected');
  if (failures.length) throw new Error(`${failures.length} clock-out(s) were not confirmed. Refresh the crew before retrying. ${failures[0].reason.message}`);
  return results.map(result => result.value);
}

// ============================================
// COST CODES
// ============================================

/**
 * Get all cost codes for an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of cost codes
 */
export async function getCostCodes(orgId) {
  const { data, error } = await supabase
    .from("t4s_cost_codes")
    .select("*")
    .eq("org_id", orgId)
    .eq("is_active", true)
    .order("code");

  if (error) {
    console.error("Error fetching cost codes:", error);
    throw error;
  }
  return data;
}

/**
 * Get cost codes for a job
 * First checks t4s_job_cost_codes for direct assignments,
 * then falls back to template-based cost codes if none found
 * @param {string} jobId - Job ID
 * @returns {Promise<Array>} List of cost codes for the job
 */
export async function getJobCostCodes(jobId) {
  // First try direct job cost codes
  const { data, error } = await supabase
    .from("t4s_job_cost_codes")
    .select(
      `
      id,
      sort_order,
      source,
      cost_code:t4s_cost_codes(
        id,
        code,
        name,
        unit
      )
    `,
    )
    .eq("job_id", jobId)
    .eq("is_active", true)
    .order("sort_order");

  if (error) throw error;

  // If we have direct job cost codes, return them
  if (data && data.length > 0) {
    return data.map((jcc) => ({
      id: jcc.cost_code.id,
      code: jcc.cost_code.code,
      name: jcc.cost_code.name,
      unit: jcc.cost_code.unit,
      source: jcc.source,
    }));
  }

  // Fall back to template-based cost codes
  // Get the job's cost_code_template
  const { data: job, error: jobError } = await supabase
    .from("t4s_jobs")
    .select("cost_code_template, org_id")
    .eq("id", jobId)
    .single();

  if (jobError || !job?.cost_code_template) {
    return [];
  }

  // Find the template by slug
  const { data: template, error: templateError } = await supabase
    .from("t4s_cost_code_templates")
    .select("id")
    .eq("org_id", job.org_id)
    .eq("slug", job.cost_code_template)
    .single();

  if (templateError || !template) {
    return [];
  }

  // Get cost codes from template
  const { data: templateItems, error: itemsError } = await supabase
    .from("t4s_cost_code_template_items")
    .select(
      `
      sort_order,
      cost_code:t4s_cost_codes(
        id,
        code,
        name,
        unit
      )
    `,
    )
    .eq("template_id", template.id)
    .order("sort_order");

  if (itemsError) {
    console.error("Error loading template items:", itemsError);
    return [];
  }

  return (templateItems || []).map((item) => ({
    id: item.cost_code.id,
    code: item.cost_code.code,
    name: item.cost_code.name,
    unit: item.cost_code.unit,
    source: "template",
  }));
}

// ============================================
// TIME ALLOCATIONS
// ============================================

/**
 * Get allocations for a time entry
 * @param {string} timeEntryId - Time entry ID
 * @returns {Promise<Array>} List of allocations
 */
export async function getAllocations(timeEntryId) {
  const { data, error } = await supabase
    .from("t4s_time_allocations")
    .select(
      `
      id,
      hours,
      notes,
      cost_code:t4s_cost_codes(
        id,
        code,
        name,
        unit
      )
    `,
    )
    .eq("time_entry_id", timeEntryId);

  if (error) throw error;
  return data;
}

/**
 * Save time allocations for an employee
 * @param {string} timeEntryId - Time entry ID
 * @param {Array} allocations - Array of { costCodeId, hours }
 * @param {string} allocatedBy - Employee ID who is allocating
 * @returns {Promise<Array>} Created allocations
 */
/**
 * Single source of truth for "fully allocated": allocations are complete
 * when they come within 0.1h (6 minutes) of the entry's total hours.
 * Mirrors the same rule in the operations-desk app.
 */
export function isEntryFullyAllocated(totalHours, allocatedHours) {
  const total = parseFloat(totalHours) || 0;
  const allocated = parseFloat(allocatedHours) || 0;
  return total > 0 && total - allocated <= 0.1;
}

export async function saveAllocations(timeEntryId, allocations, allocatedBy) {
  const payload = allocations
    .filter((a) => a.hours > 0)
    .map((a) => ({
      cost_code_id: a.costCodeId,
      hours: a.hours,
      allocated_by: allocatedBy,
    }));

  if (payload.length === 0) {
    // Just delete existing allocations
    const { error: deleteError } = await supabase
      .from("t4s_time_allocations")
      .delete()
      .eq("time_entry_id", timeEntryId);

    if (deleteError) {
      console.error("Delete error:", deleteError);
      throw deleteError;
    }
    return [];
  }

  // Atomic delete + insert in one transaction (same RPC the operations desk
  // uses) — a dropped connection can no longer wipe allocations mid-save
  const { data, error } = await supabase.rpc("t4s_save_allocations", {
    p_time_entry_id: timeEntryId,
    p_allocations: payload,
    p_allocated_by: allocatedBy,
  });

  if (error) {
    console.error("Save allocations error:", error);
    throw error;
  }

  return data;
}

// ============================================
// PHOTOS
// ============================================

/**
 * Upload a photo to Supabase Storage
 * @param {File|Blob} file - Photo file
 * @param {string} path - Storage path
 * @returns {Promise<string>} Public URL of uploaded photo
 */
export async function uploadPhoto(file, path) {
  const { data, error } = await supabase.storage
    .from("time4subs-photos")
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (error) throw error;

  // Get public URL
  const { data: urlData } = supabase.storage
    .from("time4subs-photos")
    .getPublicUrl(data.path);

  return urlData.publicUrl;
}

/**
 * Save a job photo record
 * Also syncs to PM4Subs project_photos if photo was uploaded to PM4Subs R2
 * @param {Object} params - Photo parameters
 * @returns {Promise<Object>} Created photo record
 */
export async function saveJobPhoto({
  orgId,
  jobId,
  takenBy,
  photoUrl,
  category,
  description,
  latitude,
  longitude,
  // PM4Subs fields (if photo was uploaded to PM4Subs R2)
  pm4subsPath,
  pm4subsProjectId,
  pm4subsJobNumber,
}) {
  // Save to t4s_job_photos
  const { data, error } = await supabase
    .from("t4s_job_photos")
    .insert({
      org_id: orgId,
      job_id: jobId,
      taken_by: takenBy,
      photo_url: photoUrl,
      category: category || "progress",
      description,
      latitude,
      longitude,
      taken_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;

  // Sync to PM4Subs project_photos if we have the PM4Subs path
  if (pm4subsPath && pm4subsProjectId) {
    try {
      // Map category to PM4Subs format (capitalize first letter)
      const pm4Category = category
        ? category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
        : "Progress";

      const { error: insertError } = await supabase
        .from("project_photos")
        .insert({
          project_id: pm4subsProjectId,
          project_number: pm4subsJobNumber,
          storage_path: pm4subsPath,
          file_name: pm4subsPath.split("/").pop() || "photo.jpg",
          category: pm4Category,
          description: description || null,
          latitude: latitude || null,
          longitude: longitude || null,
          created_at: new Date().toISOString(),
        });

      if (insertError) {
        console.error("Failed to insert into project_photos:", insertError);
      }
    } catch (syncError) {
      console.error("Failed to sync photo to PM4Subs:", syncError);
    }
  }

  return data;
}

// ============================================
// STATUS UPDATES
// ============================================

/**
 * Post a status update
 * @param {Object} params - Status update parameters
 * @returns {Promise<Object>} Created status update
 */
export async function postStatusUpdate({
  orgId,
  jobId,
  postedBy,
  message,
  updateType,
  photoUrl,
}) {
  const { data, error } = await supabase
    .from("t4s_status_updates")
    .insert({
      org_id: orgId,
      job_id: jobId,
      posted_by: postedBy,
      message,
      update_type: updateType || "note",
      photo_url: photoUrl,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================
// PRODUCTION
// ============================================

/**
 * Add a production entry
 * @param {Object} params - Production parameters
 * @returns {Promise<Object>} Created production entry
 */
export async function addProduction({
  orgId,
  jobId,
  costCodeId,
  quantity,
  enteredBy,
  notes,
}) {
  const workDate = new Date().toLocaleDateString("en-CA");

  const { data, error } = await supabase
    .from("t4s_production_entries")
    .insert({
      org_id: orgId,
      job_id: jobId,
      cost_code_id: costCodeId,
      work_date: workDate,
      quantity,
      entered_by: enteredBy,
      notes,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get production totals for a job
 * @param {string} jobId - Job ID
 * @returns {Promise<Array>} Production totals by cost code
 */
export async function getProductionTotals(jobId) {
  const { data, error } = await supabase
    .from("t4s_production_entries")
    .select(
      `
      cost_code_id,
      quantity,
      work_date,
      cost_code:t4s_cost_codes(code, name, unit)
    `,
    )
    .eq("job_id", jobId)
    .order("work_date", { ascending: false });

  if (error) throw error;

  // Aggregate by cost code
  const totals = {};
  const today = new Date().toLocaleDateString("en-CA");

  data.forEach((entry) => {
    const key = entry.cost_code_id;
    if (!totals[key]) {
      totals[key] = {
        costCodeId: key,
        code: entry.cost_code.code,
        name: entry.cost_code.name,
        unit: entry.cost_code.unit,
        todayQty: 0,
        totalQty: 0,
      };
    }
    totals[key].totalQty += parseFloat(entry.quantity);
    if (entry.work_date === today) {
      totals[key].todayQty += parseFloat(entry.quantity);
    }
  });

  return Object.values(totals);
}

// ============================================
// TIMESHEETS / DAILY REPORTS
// ============================================

/**
 * Get unapproved daily reports for a foreman (last 7 days only)
 */
export async function getForemanUnapprovedReports(orgId, foremanId) {
  // Show reports for current week (Monday to Sunday) - all statuses so foreman can review
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const startDate = monday.toISOString().split("T")[0];
  const endDate = sunday.toISOString().split("T")[0];

  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name, timezone),
      foreman:t4s_employees!foreman_id(id, display_name, first_name, last_name)
    `,
    )
    .eq("org_id", orgId)
    .eq("foreman_id", foremanId)
    .gte("report_date", startDate)
    .lte("report_date", endDate)
    .order("report_date", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Get ALL unapproved daily reports for an org (for admins/office)
 * Limited to current week (Monday to Sunday)
 */
export async function getAllUnapprovedReports(orgId) {
  // Show reports for current week (Monday to Sunday)
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const startDate = monday.toISOString().split("T")[0];
  const endDate = sunday.toISOString().split("T")[0];

  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name, timezone),
      foreman:t4s_employees!foreman_id(id, display_name, first_name, last_name)
    `,
    )
    .eq("org_id", orgId)
    .gte("report_date", startDate)
    .lte("report_date", endDate)
    .in("status", ["draft", "submitted", "needs_correction"])
    .order("report_date", { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Update daily report status (submit, approve, reject)
 */
export async function updateDailyReportStatus(
  reportId,
  status,
  approvedBy = null,
) {
  const updates = { status };
  if (status === "approved" && approvedBy) {
    updates.approved_by = approvedBy;
    updates.approved_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .update(updates)
    .eq("id", reportId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get time entries for a specific job and date
 */
export async function getTimeEntries(orgId, date, jobId = null) {
  let query = supabase
    .from("t4s_time_entries")
    .select(
      `
      *,
      employee:t4s_employees!employee_id(id, display_name, first_name, last_name),
      job:t4s_jobs(*),
      allocations:t4s_time_allocations(*, cost_code:t4s_cost_codes(*))
    `,
    )
    .eq("org_id", orgId)
    .eq("work_date", date);

  if (jobId) {
    query = query.eq("job_id", jobId);
  }

  query = query.neq("status", "removed");
  const { data, error } = await query.order("created_at");

  if (error) throw error;
  return data;
}

// ============================================
// DRIVER MODE
// ============================================

/**
 * Get driver's time entry for today
 */
export async function getDriverTimeEntry(orgId, employeeId, date) {
  const { data, error } = await supabase
    .from("t4s_time_entries")
    .select(
      `
      *,
      allocations:t4s_time_allocations(
        *,
        cost_code:t4s_cost_codes(*)
      )
    `,
    )
    .eq("org_id", orgId)
    .eq("employee_id", employeeId)
    .eq("work_date", date)
    .eq("entry_type", "driver")
    .maybeSingle();

  if (error) throw error;

  // If we have allocations with job_ids, fetch job info separately
  if (data?.allocations?.length > 0) {
    const jobIds = [
      ...new Set(data.allocations.map((a) => a.job_id).filter(Boolean)),
    ];
    if (jobIds.length > 0) {
      const { data: jobs } = await supabase
        .from("t4s_jobs")
        .select("id, job_number, name")
        .in("id", jobIds);

      const jobMap = {};
      jobs?.forEach((j) => (jobMap[j.id] = j));

      data.allocations = data.allocations.map((a) => ({
        ...a,
        job: a.job_id ? jobMap[a.job_id] : null,
      }));
    }
  }

  return data;
}

/**
 * Create driver time entry (no specific job - spans multiple jobs)
 */
export async function createDriverTimeEntry(entryData) {
  const { data, error } = await supabase
    .from("t4s_time_entries")
    .insert(entryData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update driver time entry (clock out, total hours)
 */
export async function updateDriverTimeEntry(entryId, updates) {
  const { data, error } = await supabase
    .from("t4s_time_entries")
    .update(updates)
    .eq("id", entryId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Create driver allocation (job stop with hours)
 */
export async function createDriverAllocation(allocationData) {
  const { data, error } = await supabase
    .from("t4s_time_allocations")
    .insert(allocationData)
    .select("*")
    .single();

  if (error) throw error;

  // Fetch job info separately if job_id is present
  if (data?.job_id) {
    const { data: job } = await supabase
      .from("t4s_jobs")
      .select("id, job_number, name")
      .eq("id", data.job_id)
      .single();

    data.job = job;
  }

  return data;
}

/**
 * Delete driver allocation (remove job stop)
 */
export async function deleteDriverAllocation(allocationId) {
  const { error } = await supabase
    .from("t4s_time_allocations")
    .delete()
    .eq("id", allocationId);

  if (error) throw error;
}

// ============================================
// HELPERS
// ============================================

/**
 * Calculate distance between two GPS coordinates (in miles)
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Sort jobs by distance from current location
 */
export function sortJobsByDistance(jobs, currentLat, currentLon) {
  return jobs
    .map((job) => ({
      ...job,
      distance:
        job.latitude && job.longitude
          ? calculateDistance(
              currentLat,
              currentLon,
              job.latitude,
              job.longitude,
            )
          : Infinity,
    }))
    .sort((a, b) => a.distance - b.distance);
}

// ============================================
// RECEIPTS
// ============================================

/**
 * Save a receipt record
 * @param {Object} params - Receipt parameters
 * @returns {Promise<Object>} Created receipt record
 */
export async function saveReceipt({
  orgId,
  uploadedBy,
  storagePath,
  jobId,
  fieldNote,
  source = "field",
}) {
  const { data, error } = await supabase
    .from("t4s_receipts")
    .insert({
      org_id: orgId,
      uploaded_by: uploadedBy,
      storage_path: storagePath,
      job_id: jobId,
      field_note: fieldNote || null,
      status: "pending",
      source,
      created_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get receipts for a job
 * @param {string} jobId - Job ID
 * @param {string} status - Optional status filter
 * @returns {Promise<Array>} List of receipts
 */
export async function getJobReceipts(jobId, status = null) {
  let query = supabase
    .from("t4s_receipts")
    .select(
      `
      *,
      uploader:t4s_employees!uploaded_by(id, display_name, first_name, last_name),
      job:t4s_jobs!job_id(id, job_number, name)
    `,
    )
    .eq("job_id", jobId)
    .order("created_at", { ascending: false });

  if (status) {
    query = query.eq("status", status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/**
 * Get all pending receipts for an org
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of pending receipts
 */
export async function getPendingReceipts(orgId) {
  const { data, error } = await supabase
    .from("t4s_receipts")
    .select(
      `
      *,
      uploader:t4s_employees!uploaded_by(id, display_name, first_name, last_name),
      job:t4s_jobs!job_id(id, job_number, name)
    `,
    )
    .eq("org_id", orgId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data;
}

// ============================================
// VOCABULARY
// ============================================

/**
 * Load org vocabulary for voice commands (top 50 by usage)
 */
export async function loadVocabulary(orgId) {
  if (!orgId) return [];

  const { data, error } = await supabase
    .from("t4s_org_vocabulary")
    .select("term, term_type, resolves_to, employee_id, cost_code_id")
    .eq("org_id", orgId)
    .eq("is_active", true)
    .order("usage_count", { ascending: false })
    .limit(50);

  if (error) {
    console.error("Error loading vocabulary:", error);
    return [];
  }

  return data || [];
}

/**
 * Add a new vocabulary term
 */
export async function addVocabularyTerm({
  orgId,
  term,
  termType,
  resolvesTo,
  employeeId = null,
  costCodeId = null,
  createdBy = null,
}) {
  const { data, error } = await supabase
    .from("t4s_org_vocabulary")
    .insert({
      org_id: orgId,
      term: term.toLowerCase().trim(),
      term_type: termType,
      resolves_to: resolvesTo,
      employee_id: employeeId,
      cost_code_id: costCodeId,
      created_by: createdBy,
    })
    .select()
    .single();

  if (error) {
    console.error("Error adding vocabulary term:", error);
    throw error;
  }

  return data;
}

/**
 * Increment usage count for a vocabulary term
 */
export async function incrementVocabularyUsage(termId) {
  const { error } = await supabase.rpc("increment_vocabulary_usage", {
    vocab_id: termId,
  });

  if (error) {
    console.error("Error incrementing vocabulary usage:", error);
  }
}

export default supabase;
